import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Download, Upload } from "lucide-react";
import { PageLoader } from "@/components/common/loader";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { downloadPortalDocument, listPublishedDocuments } from "@/features/portal/api";
import { portalHref } from "@/features/portal/portal-base";
import type { PortalDocument } from "@/features/portal/types";

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
}

/** `/portal/documents` — documents the firm published to this client. */
export function PortalDocuments() {
  const [docs, setDocs] = useState<PortalDocument[] | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    listPublishedDocuments()
      .then((list) => {
        if (active) setDocs(list);
      })
      .catch((err) => {
        if (active) setDocs([]);
        toast.error(err?.message ?? "Could not load documents.");
      });
    return () => {
      active = false;
    };
  }, []);

  async function handleDownload(doc: PortalDocument) {
    setDownloadingId(doc.id);
    try {
      const blob = await downloadPortalDocument(doc.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error((err as { message?: string })?.message ?? "Download failed.");
    } finally {
      setDownloadingId(null);
    }
  }

  if (docs === null) return <PageLoader />;

  return (
    <div className="mx-auto w-full max-w-3xl space-y-6 py-8">
      <div className="flex items-center justify-between gap-4">
        <h1 className="text-2xl font-semibold text-foreground">Documents</h1>
        <Button asChild variant="outline">
          <Link to={portalHref("/documents/upload")}>
            <Upload className="size-4" />
            Upload Documents
          </Link>
        </Button>
      </div>

      {docs.length === 0 ? (
        <Card>
          <div className="p-8 text-center text-muted-foreground">No documents published.</div>
        </Card>
      ) : (
        <Card className="gap-0 overflow-hidden p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Uploaded</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {docs.map((doc) => (
                <TableRow key={doc.id}>
                  <TableCell className="font-medium text-foreground">{doc.name}</TableCell>
                  <TableCell className="text-muted-foreground">
                    {formatDate(doc.createdAt)}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={downloadingId === doc.id}
                      onClick={() => handleDownload(doc)}
                    >
                      <Download className="size-4" />
                      Download
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}
